import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCoverageMatrix } from '../services/api';
import './CoverageMatrixPage.css';

// NER-1778 (FE): matriz profesionales (filas) × coberturas (columnas). Cada celda
// refleja el estado del backend (buildCoverageMatrix): acepta con precio propio
// (verde) / acepta y hereda el ajuste default (gris) / no acepta (rojo).
const DEFAULT_LABEL = 'Cobertura';

const cellState = (cell) => {
  if (!cell || !cell.accepted) return 'reject';
  if (cell.priceOverride != null) return 'accept';
  return 'inherit';
};

function CoverageMatrixPage() {
  const { t } = useTranslation();

  const [coverages, setCoverages] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [coverageLabel, setCoverageLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const label = coverageLabel || DEFAULT_LABEL;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getCoverageMatrix();
      const m = res?.matrix || {};
      setCoverages(m.coverages || []);
      setProfessionals(m.professionals || []);
      setCoverageLabel(m.coverageLabel || '');
    } catch (err) {
      console.error('Error loading coverage matrix:', err);
      setError(t('coverageMatrix.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Mapa coverageKey -> celda, por profesional (el array de celdas es paralelo al
  // master pero lo indexamos por key para no depender del orden).
  const cellByKey = (prof) => {
    const map = {};
    (prof.coverages || []).forEach((c) => {
      map[c.coverageKey] = c;
    });
    return map;
  };

  return (
    <div className="list-page">
      <div className="page-header">
        <h1>{t('coverageMatrix.title')}</h1>
      </div>
      <p className="list-page-subtitle">{t('coverageMatrix.subtitle', { label })}</p>

      {error && <div className="error-message">{error}</div>}

      <div className="coverage-matrix-legend">
        <span className="cm-legend-item">
          <span className="cm-swatch cm-swatch--accept" />
          {t('coverageMatrix.legendAccept')}
        </span>
        <span className="cm-legend-item">
          <span className="cm-swatch cm-swatch--inherit" />
          {t('coverageMatrix.legendInherit')}
        </span>
        <span className="cm-legend-item">
          <span className="cm-swatch cm-swatch--reject" />
          {t('coverageMatrix.legendReject')}
        </span>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : professionals.length === 0 || coverages.length === 0 ? (
          <div className="empty-state">{t('coverageMatrix.empty')}</div>
        ) : (
          <div className="coverage-matrix-scroll">
            <table className="coverage-matrix-table">
              <thead>
                <tr>
                  <th className="cm-corner">{t('coverageMatrix.professional')}</th>
                  {coverages.map((c) => (
                    <th key={c.key} className="cm-col-head" title={c.name}>
                      <span className="cm-col-label">{c.name}</span>
                      {c.isParticular && (
                        <span className="cm-col-particular">{t('coverages.particular')}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {professionals.map((prof) => {
                  const map = cellByKey(prof);
                  return (
                    <tr key={prof.professionalId}>
                      <th className="cm-row-head" title={prof.name || ''}>
                        {prof.name || prof.professionalId}
                      </th>
                      {coverages.map((c) => {
                        const cell = map[c.key];
                        const state = cellState(cell);
                        return (
                          <td
                            key={c.key}
                            className={`cm-cell cm-cell--${state}`}
                            title={t(`coverageMatrix.legend${state.charAt(0).toUpperCase() + state.slice(1)}`)}
                          >
                            {state === 'accept' && cell.priceOverride != null ? (
                              <span className="cm-cell-price">${cell.priceOverride}</span>
                            ) : (
                              <span className="cm-cell-dot" aria-hidden="true" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoverageMatrixPage;
